if (!customElements.get('author-articles')) {
  class AuthorArticles extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.grid = this.querySelector('[data-article-grid]');
      this.button = this.querySelector('[data-article-more]');
      this.status = this.querySelector('[data-article-status]');
      this.empty = this.querySelector('[data-article-empty]');
      this.pagination = this.querySelector('[data-article-pagination]');
      if (!this.grid || !this.button) return;
      this.cards = [...this.grid.querySelectorAll('[data-article-id]')];
      this.ids = new Set(this.cards.map((card) => card.dataset.articleId));
      this.visited = new Set();
      this.nextUrl = this.dataset.nextUrl;
      this.pageSize = Number(this.dataset.pageSize) || 12;
      this.shown = 0;
      this.moreLabel = this.button.textContent;
      this.pagination.hidden = true;
      this.button.addEventListener('click', () => this.showMore(), { signal: this.controller.signal });
      this.showMore(true);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.loading = false;
    }

    async showMore(initial = false) {
      if (this.loading) return;
      this.loading = true;
      this.button.disabled = true;
      this.status.textContent = this.dataset.loading;
      this.empty.hidden = true;
      const previousCount = this.shown;
      const targetCount = this.shown + this.pageSize;
      const signal = this.controller.signal;
      try {
        // Empty batches are normal: keep scanning until this author's next row is full.
        while (this.cards.length < targetCount && this.nextUrl) {
          await this.fetchNextPage(signal);
        }
        if (signal.aborted) return;
        this.shown = Math.min(targetCount, this.cards.length);
        this.render();
        if (!initial && this.shown > previousCount) {
          this.cards[previousCount].querySelector('a').focus();
        } else if (!initial && this.button.hidden) {
          this.status.focus();
        }
      } catch (error) {
        if (signal.aborted) return;
        this.shown = Math.min(targetCount, this.cards.length);
        this.render();
        this.status.textContent = this.dataset.error;
        this.button.textContent = this.dataset.retry;
        this.button.hidden = false;
        // Keep crawlable page links available if a request fails or JS is unavailable.
        this.pagination.hidden = false;
      } finally {
        if (!signal.aborted) {
          this.loading = false;
          this.button.disabled = false;
        }
      }
    }

    async fetchNextPage(signal) {
      // Start from the current URL to retain preview-theme and locale context.
      const url = new URL(window.location.href);
      const next = new URL(this.nextUrl, url);
      if (next.origin !== url.origin || next.pathname !== url.pathname) {
        throw new Error('Unexpected author archive URL');
      }
      next.searchParams.forEach((value, key) => url.searchParams.set(key, value));
      url.searchParams.delete('sections');
      url.searchParams.set('section_id', this.dataset.sectionId);
      url.hash = '';
      if (this.visited.has(url.href)) throw new Error('Repeated author archive page');
      const response = await fetch(url, { signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Unable to load author archive');
      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      const archive = document.querySelector('author-articles');
      if (!archive || archive.dataset.authorId !== this.dataset.authorId) {
        throw new Error('Unexpected author archive response');
      }
      archive.querySelectorAll('[data-article-id]').forEach((card) => {
        if (!this.ids.has(card.dataset.articleId)) {
          const imported = window.document.importNode(card, true);
          imported.hidden = true;
          this.grid.append(imported);
          this.cards.push(imported);
          this.ids.add(card.dataset.articleId);
        }
      });
      this.visited.add(url.href);
      this.nextUrl = archive.dataset.nextUrl;
    }

    render() {
      this.cards.forEach((card, index) => { card.hidden = index >= this.shown; });
      this.empty.hidden = this.shown > 0;
      this.empty.textContent = this.dataset.empty;
      this.status.textContent = this.shown ? this.dataset.showing.replace('[count]', this.shown) : '';
      this.button.textContent = this.moreLabel;
      this.button.hidden = this.shown >= this.cards.length && !this.nextUrl;
      this.pagination.hidden = true;
    }
  }
  customElements.define('author-articles', AuthorArticles);
}
